import { datadogLogs } from '@datadog/browser-logs';
import { logContext } from '@shared/services';
import { useMutation } from '@tanstack/react-query';
import { SESSION_NOTE_API_REQUEST_TIMEOUT_MS } from '@web-app/constants/timeouts';
import { useApiClient } from '@web-app/hooks/useApiClient';
import { Note } from '@web-app/server/api/types';
import { stripSessionNoteServerManagedFields } from '@web-app/utils/api/session-notes/session-note-save-fields';
import { AuthenticatedApiClient } from '@web-app/utils/api-client';
import { isAxiosError } from 'axios';

export const stripServerManagedSessionNoteFields = (data: Note): Note => {
  if (!data.sessionNote) {
    return data;
  }

  return {
    ...data,
    sessionNote: stripSessionNoteServerManagedFields(data.sessionNote),
  };
};

export const saveSessionNote = async (
  data: Note,
  apiClient: AuthenticatedApiClient,
): Promise<Note> => {
  // The API endpoint supports both UUID and dailyRoomName lookups
  const identifier = data.sessionNote?.id || data.sessionNote?.dailyRoomName;
  const dataToSave = stripServerManagedSessionNoteFields(data);

  if (!identifier) {
    throw new Error(
      'Cannot save session note: missing both id and dailyRoomName',
    );
  }

  try {
    const res = await apiClient.put<Note>(
      `/session-notes/${identifier}/note`,
      dataToSave,
      { timeout: SESSION_NOTE_API_REQUEST_TIMEOUT_MS },
    );
    return res.data;
  } catch (error) {
    const apiMessage =
      isAxiosError(error) && typeof error.response?.data?.error === 'string'
        ? error.response.data.error
        : undefined;
    const normalizedError = apiMessage ? new Error(apiMessage) : error;

    datadogLogs.logger.error(
      'Failed to save session note',
      logContext('session-note', 'save', {
        detail: 'Provider note save failed; drawer shows retryable error state',
        identifier,
        sessionNoteId: data.sessionNote?.id,
        dailyRoomName: data.sessionNote?.dailyRoomName,
        error:
          normalizedError instanceof Error
            ? normalizedError.message
            : String(normalizedError),
      }),
      normalizedError instanceof Error ? normalizedError : undefined,
    );
    throw normalizedError;
  }
};

export const useSaveSessionNote = () => {
  const apiClient = useApiClient();

  return useMutation<Note, Error, Note>({
    mutationKey: ['berrystreetSaveSessionNote'],
    mutationFn: (data: Note) => saveSessionNote(data, apiClient),
    // The interceptor handles up to 2 auth-refresh retries internally.
    // RQ retry on top would add no value for this idempotent PUT and
    // could cause up to 9 attempts in the worst case (3 RQ × 3 interceptor).
    retry: false,
  });
};

export const useSaveSessionNoteImmediate = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: Note) => void;
  onError?: (error: Error) => void;
}) => {
  const apiClient = useApiClient();

  return useMutation<Note, Error, Note>({
    mutationKey: ['berrystreetSaveSessionNoteImmediate'],
    mutationFn: (data: Note) => saveSessionNote(data, apiClient),
    onSuccess,
    onError,
    retry: false,
  });
};
